import React, { useState, useRef, useMemo, useCallback, useImperativeHandle } from 'react';
import _ from 'lodash';
import LoadingIndicator from '../common/loading_indicator.jsx';
import Toolbar from './Toolbar.jsx';
import HeaderComponent from './HeaderComponent.jsx';

import AgGrid, { AgGridWrapper, getLocaleText } from '../common/AgGrid/index.jsx';
import { restoreDefaultPreset, restorePreset } from './Presets.jsx';
import useDynamicCols, { useDynamicDefaultCols } from './hooks/useDynamicCols.jsx';
import useCustomAggFuncs from './hooks/useCustomAggFuncs.js';
import useRescaleColumns from './hooks/useRescaleColumns.js';
import useSidePanel from './hooks/useSidePanel.js';
import useRowId from './hooks/useRowId.js';
import AutoGroupValueRenderer from './AutoGroupValueRenderer.jsx';
import { useSelectionColumnClick } from './hooks/useSelectionColumnClick.js';

// Dynamic props specific to datagrid
const DatagridFormComponent = React.forwardRef(({
  datagrid, 
  context, 
  wrapperStyle, 
  supressToolbar = false,
  supressBulkActions = false,
  supressSorting = false,
  editable = false,
  useDefaultPreset = false,
  columnsTotalsToDisplay = [],
  agGridProps = {},
  rowDataProps,
  presetId = null
}, ref) => {

  const gridRef = useRef();
  const wrapperRef = useRef();
  const rescaleColumns = useRescaleColumns(gridRef, wrapperRef);

  const unsavedTransactions = useRef([]);
  const transactionQueue = useRef({data: {}, updates: {}});
  const cellUpdatesTimeout = useRef(null);

  const [editMode, setEditMode] = useState(editable);
  const [rowData, setRowData] = useState(rowDataProps || [])

  const initialColumnDefs = useDynamicCols(
    datagrid.columns || [], 
    {
      editMode: editMode, 
      canModifyNonEditableRows: context?.permissions?.modify_non_editable_rows, 
      supressBulkActions: !editMode 
    });

  const components = useMemo(() => {
    return {
      agColumnHeader: HeaderComponent,
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      width: 180,
      cellRendererParams: {
        innerRenderer: AutoGroupValueRenderer
      }
    };
  }, []);

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useDynamicDefaultCols(context, editMode, supressSorting);

  const [createRow, getRowId] = useRowId();

  const addNewRow = () => {
    const rowIndex = gridRef.current.api.getDisplayedRowCount();
    const newRow = createRow();
    const transaction = { addIndex: rowIndex, add: [newRow] };

    gridRef.current.api.applyTransaction(transaction);
    unsavedTransactions.current.push(transaction);

    setRowData(prevRowData => [...prevRowData, newRow]);

    gridRef.current.api.startEditingCell({
      rowIndex: rowIndex,
      colKey: initialColumnDefs[1].field
    });
  };

  const removeSelectedRow = () => {
    const selectedNodes = gridRef.current.api.getSelectedNodes();

    if (selectedNodes.length == 0) return;
    if (!confirm(I18n.t('confirm_row_delete'))) return;

    const selectedIds = selectedNodes.map(row => row.data.id);
    const filteredRowData = rowData.filter((row) => !selectedIds.includes(row?.id));
    setRowData([...filteredRowData]);

    const transaction = { remove: selectedNodes.map(row => ({id: row.data.id})) };
    unsavedTransactions.current.push(transaction);
  };

  // Value Change Detection - When a value for any cell changes
  const onCellEdited = useCallback((event) => {
    const oldData = event.data;
    const field = event.colDef.field;

    // Update transactionQueue with accumulated changes for each row
    transactionQueue.current.data[oldData.id] = oldData;
    transactionQueue.current.updates[oldData.id] = {...(transactionQueue.current?.updates[oldData.id]|| {}), [field]: event.newValue, changed: true};

    if(cellUpdatesTimeout.current) clearTimeout(cellUpdatesTimeout.current);
    cellUpdatesTimeout.current = setTimeout(() => processTransactionQueue(), 1);
  }, []);

  const processTransactionQueue = useCallback(() => {
    cellUpdatesTimeout.current = null;

    if (!Object.keys(transactionQueue.current?.updates)?.length) return;

    // Accumulate changes from transactionQueue into a single transaction
    const updatedQueue = Object.keys(transactionQueue.current.updates).map(rowId => (
        {...transactionQueue.current.data[rowId], ...transactionQueue.current.updates[rowId]}
    ));

    // Add the transaction to the unsavedTransactions array
    const transaction = {update: updatedQueue};
    unsavedTransactions.current.push(transaction);

    transactionQueue.current = ({data: {}, updates: {}});
  }, []);

  const loadingOverlayComponent = useMemo(() => {
    return LoadingIndicator;
  }, []);

  const gridReady = (event) => {
    if (typeof agGridProps.gridReady == 'function') agGridProps.gridReady(event);

    if (useDefaultPreset) restoreDefaultPreset(datagrid, gridRef);
    else if (presetId) {
      const preset = datagrid.presets.find(p => p.id === presetId);
      if (preset) {
        restorePreset(preset, gridRef);
      }
    }

    rescaleColumns();
    displayColumnTotals();
  };

  const aggregateTotals = (arr, keys) => {
    return keys.reduce((totals, key) => {
        totals[key] = arr.reduce((sum, obj) => {
            let value = obj[key];
            // Check if the value is a valid number and add it to the sum
            return sum + (value !== '' && value !== null && value !== undefined ? Number(value) : 0);
        }, 0);
        return totals;
    }, {});
  };

  const displayColumnTotals = () => {
    if (columnsTotalsToDisplay.length && gridRef.current.api) {
      let totals = aggregateTotals(rowData, columnsTotalsToDisplay);
      gridRef.current.api.setGridOption('pinnedBottomRowData', [totals]);

      $('.ag-floating-bottom .ag-column-first').html(I18n.t('total'));
      $('.ag-floating-bottom .ag-row-pinned').css('background','#edfaf1');
    }
  };

  const aggFuncs = useCustomAggFuncs(datagrid.columns, initialColumnDefs);

  useImperativeHandle(ref, () => ({
    getRowData() {
      let rowData = []
      gridRef.current.api?.forEachNode(node => rowData.push(node.data))
      return rowData;
    },
    getUnsavedTransactions() {
      return unsavedTransactions.current;
    },
    setRowData(rowData) {
      setRowData(rowData)
    }
  }));

  const sideBar = useSidePanel({context, toolPanelParams: {
    datagrid: datagrid,
    suppressPivotMode: true
  }});

  const statusBar = useMemo(() => { 
    return {
          statusPanels: 
          [
            { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
            { 
              statusPanel: 'agAggregationComponent',
              statusPanelParams: {
                aggFuncs: ['sum', 'avg']
              } 
            },
          ]          
      };
  }, []);

  const rowSelection = useMemo(() => { 
    return { 
          mode: 'multiRow',
          isRowSelectable: () => editable
      };
  }, [editable]);
  
  return (
    <AgGridWrapper 
      ref={wrapperRef}
      wrapperStyle={{
        height: '60vh',
        ...wrapperStyle        
      }}
    >
      {!supressToolbar && <Toolbar 
        editMode={editMode} 
        setEditMode={setEditMode} 
        gridRef={gridRef} 
        addRow={addNewRow} 
        removeSelectedRows={removeSelectedRow}
        datagrid={datagrid}
        context={context}
        toggleStatusBarComp={useCallback(() => {}, [])}
      />}
      <AgGrid
          ref={gridRef} // Ref for accessing Grid's API
          columnDefs={initialColumnDefs}
          autoGroupColumnDef={autoGroupColumnDef}
          rowData={rowData}
          style={{flex: 1}}
          context={context}
          defaultColDef={defaultColDef}
          animateRows={true}
          aggFuncs={aggFuncs}
          rowGroupPanelShow = {true}
          getRowId={getRowId}
          cellSelection={true}
          loadingOverlayComponent={loadingOverlayComponent}
          groupDisplayType='multipleColumns'
          sideBar={!editMode && sideBar}
          {...agGridProps}
          {...(editable ? { rowSelection } : {})}
          onGridReady={gridReady}
          statusBar={statusBar} 
          stopEditingWhenCellsLoseFocus
          components = {components}
          onCellEditRequest={onCellEdited}
          onCellValueChanged={onCellEdited}
          getLocaleText={getLocaleText}
          onCellClicked={useSelectionColumnClick()}
        />
    </AgGridWrapper>
  )

});

export default DatagridFormComponent;